import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CLIENT_ORIGINS, CLIENT_TYPES, type Client, type ClientOrigin, type ClientType } from '@/types';
import { useCreateClient, useUpdateClient } from '@/hooks/useClients';
import { toast } from 'sonner';
import { Plus, X } from 'lucide-react';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  client?: Client | null;
}

export function ClientFormDialog({ open, onOpenChange, client }: Props) {
  const [name, setName] = useState('');
  const [origin, setOrigin] = useState<ClientOrigin>('Onshore');
  const [type, setType] = useState<ClientType>('MFO');
  const [contacts, setContacts] = useState<{ name: string; email: string; phone: string; observacoes: string }[]>([
    { name: '', email: '', phone: '', observacoes: '' },
  ]);
  const [role, setRole] = useState('');
  const [address, setAddress] = useState('');
  const [responsavelJusc, setResponsavelJusc] = useState('');

  const createClient = useCreateClient();
  const updateClient = useUpdateClient();

  useEffect(() => {
    if (client) {
      setName(client.name);
      setOrigin(client.origin);
      setType(client.type);
      const pocs = client.person_of_contact || [];
      const emails = client.email || [];
      const phones = client.phone || [];
      const observacoes = client.observacoes || [];
      const merged = pocs.map((p, i) => ({
        name: p,
        email: emails[i] || '',
        phone: phones[i] || '',
        observacoes: observacoes[i] || '',
      }));
      setContacts(merged.length > 0 ? merged : [{ name: '', email: '', phone: '', observacoes: '' }]);
      setRole(client.role || '');
      setAddress(client.address || '');
      setResponsavelJusc(client.responsavel_jusc);
    } else {
      setName(''); setOrigin('Onshore'); setType('MFO');
      setContacts([{ name: '', email: '', phone: '', observacoes: '' }]);
      setRole(''); setAddress(''); setResponsavelJusc('');
    }
  }, [client, open]);

  const addContact = () => setContacts([...contacts, { name: '', email: '', phone: '', observacoes: '' }]);
  const removeContact = (i: number) => setContacts(contacts.filter((_, idx) => idx !== i));
  const updateContact = (i: number, field: 'name' | 'email' | 'phone' | 'observacoes', value: string) => {
    const updated = [...contacts];
    updated[i] = { ...updated[i], [field]: value };
    setContacts(updated);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Mantém as 4 listas com o MESMO tamanho (uma entrada por linha de contato,
    // mesmo que vazia) — se filtrarmos cada campo separadamente, um contato sem
    // telefone desloca os índices e o telefone acaba "colado" na pessoa errada.
    const filledContacts = contacts.filter(
      c => c.name.trim() || c.email.trim() || c.phone.trim() || c.observacoes.trim()
    );
    const data = {
      name: name.toUpperCase(), origin, type,
      person_of_contact: filledContacts.map(c => c.name.trim()),
      role: role || null,
      email: filledContacts.map(c => c.email.trim()),
      phone: filledContacts.map(c => c.phone.trim()),
      observacoes: filledContacts.map(c => c.observacoes.trim()),
      address: address.trim() || null,
      responsavel_jusc: responsavelJusc,
    };

    try {
      if (client) {
        await updateClient.mutateAsync({ id: client.id, ...data });
        toast.success('Client updated successfully');
      } else {
        await createClient.mutateAsync(data);
        toast.success('Client created successfully');
      }
      onOpenChange(false);
    } catch {
      toast.error('Error saving client');
    }
  };

  const isLoading = createClient.isPending || updateClient.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg bg-card border-border max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            {client ? 'Edit Client' : 'New Client'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Investor</Label>
            <Input value={name} onChange={e => setName(e.target.value)} required placeholder="Investor name" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Group</Label>
              <Select value={origin} onValueChange={v => setOrigin(v as ClientOrigin)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CLIENT_ORIGINS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={type} onValueChange={v => setType(v as ClientType)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CLIENT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Dynamic contacts: name, email, phone, observações */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Contacts</Label>
              <Button type="button" variant="ghost" size="sm" onClick={addContact} className="gap-1 h-7 text-xs">
                <Plus className="h-3 w-3" /> Add
              </Button>
            </div>
            {contacts.map((contact, i) => (
              <div key={i} className="rounded-md border border-border p-3 space-y-2 relative">
                {contacts.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeContact(i)}
                    className="absolute top-1 right-1 h-7 w-7"
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                )}
                <div className="flex gap-2 pr-8">
                  <Input
                    placeholder="Contact name"
                    value={contact.name}
                    onChange={e => updateContact(i, 'name', e.target.value)}
                    className="flex-1"
                  />
                </div>
                <div className="flex gap-2">
                  <Input
                    type="email"
                    placeholder="email@example.com"
                    value={contact.email}
                    onChange={e => updateContact(i, 'email', e.target.value)}
                    className="flex-1"
                  />
                  <Input
                    type="tel"
                    placeholder="Phone"
                    value={contact.phone}
                    onChange={e => updateContact(i, 'phone', e.target.value)}
                    className="flex-1"
                  />
                </div>
                <div className="flex gap-2">
                  <Input
                    placeholder="Observações"
                    value={contact.observacoes}
                    onChange={e => updateContact(i, 'observacoes', e.target.value)}
                    className="flex-1"
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="space-y-2">
            <Label>Address <span className="text-muted-foreground text-xs">(optional)</span></Label>
            <Textarea
              value={address}
              onChange={e => setAddress(e.target.value)}
              rows={2}
              placeholder={"Av. Paulista, 1000 - 2o. andar\n04545-000 - São Paulo - SP"}
              className="min-h-0 resize-y"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Role <span className="text-muted-foreground text-xs">(optional)</span></Label>
              <Input value={role} onChange={e => setRole(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>POC Jus</Label>
              <Input value={responsavelJusc} onChange={e => setResponsavelJusc(e.target.value)} required />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Saving...' : client ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
